import { fetchAuthFeature } from '../apis/msBackendAuth.api'
import type { FeatureFlag } from '../types/auth.types'

const BACKEND_TO_FRONTEND_MAP: Record<string, string> = {
  MSAL_WEB: 'MS_BACKEND',
  COGNITO: 'COGNITO',
  MSAL_SPA: 'MSAL',
}

/** The `key` this module cares about within the `/auth/feature` array. */
const LOGIN_FEATURE_KEY = 'login'

const SESSION_KEY = '_iam_auth_provider'

/** Module-level cache so the value survives hot-module reloads within the same tab. */
let _cached: string | null = null

/**
 * Returns the resolved auth provider (e.g. "MS_BACKEND", "COGNITO", "MSAL").
 * Reads from the module cache first, then sessionStorage, then returns null.
 */
export function getResolvedAuthProvider(): string | null {
  return _cached ?? sessionStorage.getItem(SESSION_KEY)
}

function setResolvedAuthProvider(provider: string): void {
  _cached = provider
  sessionStorage.setItem(SESSION_KEY, provider)
}

/**
 * `GET /auth/feature` returns an array of feature flags, not just login config —
 * e.g. `[{ key: "login", value: "MSAL_SPA", environmentType: "MSAL_SPA" }]`.
 * `key` identifies a flag's purpose, `value` is that flag's value. This endpoint
 * can grow to carry unrelated flags in the future; look up one flag by `key`.
 */
function findFeatureValue(raw: unknown, key: string): string | undefined {
  if (typeof raw === 'string') return raw.trim()
  if (!Array.isArray(raw)) return undefined
  const match = (raw as FeatureFlag[]).find((flag) => flag?.key === key)
  return typeof match?.value === 'string' ? match.value : undefined
}

/**
 * Fetch `/public/api/v1/auth/feature`, look up the "login" flag, map its value
 * to a frontend provider string, cache the result, and return it.
 *
 * ALWAYS hits the API — the backend is the source of truth for which provider is
 * active, so we must not short-circuit on a previously cached value (that would
 * pin the app to whatever provider was resolved first and ignore later changes).
 * The cache is only used as a synchronous fallback for `getResolvedAuthProvider()`
 * before this promise settles, and as a last resort if the request fails.
 *
 * On failure, falls back to the build-time env provider (`fallback`), then to
 * any previously cached value. Should be called once on app boot before any
 * auth guard runs.
 */
export async function fetchAndResolveAuthFeature(
  fallback: string
): Promise<string> {
  try {
    const raw = await fetchAuthFeature()
    const loginValue = findFeatureValue(raw, LOGIN_FEATURE_KEY)
    const mapped = loginValue ? BACKEND_TO_FRONTEND_MAP[loginValue] : undefined
    if (!mapped) {
      console.warn(
        '[authFeature] Unrecognized response from /auth/feature — falling back to',
        fallback,
        'Raw response:',
        raw
      )
    }
    const resolved = mapped ?? fallback
    console.info(`[authFeature] login mode resolved: ${resolved}`)
    setResolvedAuthProvider(resolved)
    return resolved
  } catch (err) {
    // /feature is the source of truth, but when it's unreachable fall back to
    // the build-time env provider (VITE_AUTH_PROVIDER) FIRST — not a stale
    // cached value from an earlier successful call. Preferring the cache here
    // pinned the tab to whatever provider resolved first (e.g. MS_BACKEND):
    // when /feature failed because the backend was down, the guard stayed on
    // MS_BACKEND and boot bounced through Microsoft login in a loop, never
    // honouring the local env. For a correctly-configured deployment the env
    // already matches what /feature returns, so env-first is safe there too;
    // the cache is kept only as a secondary if no env provider is configured.
    const resolved = fallback || getResolvedAuthProvider() || ''
    console.warn(
      '[authFeature] /auth/feature request failed — falling back to',
      resolved || '(no provider configured)',
      err
    )
    // Only persist a real value; caching '' would make getResolvedAuthProvider
    // return '' (falsy but not nullish), defeating the `?? env` fallbacks.
    if (resolved) setResolvedAuthProvider(resolved)
    return resolved
  }
}
